/**
 * Payment Method Breakdown Chart
 * Shows distribution of Cash vs Transfer vs POS for manual bookings
 */

import { CURRENCY_CODE } from '../../utils/currency';
import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { DollarSign } from 'lucide-react';

interface PaymentMethodData {
  method: string;
  bookings: number;
  revenue: number;
  percentage: number;
}

interface PaymentMethodChartProps {
  data: PaymentMethodData[];
}

const COLORS = {
  cash: '#10b981',      // Green
  transfer: '#3b82f6',  // Blue
  pos: '#8b5cf6',       // Purple
};

const METHOD_LABELS = {
  cash: '💵 Cash',
  transfer: '🏦 Transfer',
  pos: '💳 POS',
};

export function PaymentMethodChart({ data }: PaymentMethodChartProps) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
        </div>
        <div className="text-center py-12 text-gray-500">
          <p>No manual booking data available</p>
          <p className="text-sm mt-1">Add manual bookings to see payment method breakdown</p>
        </div>
      </div>
    );
  }

  const chartData = data.map((item) => ({
    name: METHOD_LABELS[item.method as keyof typeof METHOD_LABELS] || item.method,
    value: item.revenue,
    bookings: item.bookings,
    percentage: item.percentage,
    method: item.method,
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: CURRENCY_CODE,
      minimumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-900 mb-1">{data.name}</p>
          <p className="text-sm text-gray-600">
            Revenue: <span className="font-semibold text-green-600">{formatCurrency(data.value)}</span>
          </p>
          <p className="text-sm text-gray-600">
            Bookings: <span className="font-semibold">{data.bookings}</span>
          </p>
          <p className="text-sm text-gray-600">
            Share: <span className="font-semibold">{data.percentage}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <DollarSign className="w-5 h-5 text-green-600" />
        <h3 className="text-lg font-semibold text-gray-900">Payment Methods</h3>
        <span className="text-xs text-gray-500 ml-auto">Manual Bookings Only</span>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }: any) => `${name?.split(' ')[1] ?? name} ${(percent * 100).toFixed(0)}%`}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[entry.method as keyof typeof COLORS] || '#94a3b8'}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value, entry: any) => (
              <span className="text-sm">
                {value} - {formatCurrency(entry.payload.value)}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Summary Stats */}
      <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-3 gap-4">
        {chartData.map((item) => (
          <div key={item.method} className="text-center">
            <div className="text-xs text-gray-500 mb-1">{item.name}</div>
            <div className="font-semibold text-gray-900">{item.bookings} bookings</div>
            <div className="text-xs text-gray-600">{item.percentage}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}
